import DOMPurify from 'dompurify';
import type { Config as DomPurifyConfig } from 'dompurify';
import type { OwaSanitizerConfig } from './owaSanitizerConfig';
import { getSanitizerConfigAdapter } from './adapters/SanitizerConfigAdapter';
import { getAllowedUriRegExp } from './adapters/uriPostProcessor';
import { SanitizerReturnType } from './sanitizerReturnType';

/**
 * Builds a DOMPurify config from the OWA extended config, including URI filtering.
 */
function buildDomPurifyConfig(config: OwaSanitizerConfig): DomPurifyConfig {
    const domPurifyConfig = getSanitizerConfigAdapter().toDomPurifyConfig(config);
    if (!config.allowUnknownProtocols) {
        domPurifyConfig.ALLOWED_URI_REGEXP = getAllowedUriRegExp();
    }
    return domPurifyConfig;
}

/**
 * DOMPurify string path: sanitize and return a plain string.
 */
function sanitizeToString(html: string, domPurifyConfig: DomPurifyConfig): string {
    return DOMPurify.sanitize(html, domPurifyConfig);
}

/**
 * DOMPurify TrustedHTML path: sanitize and keep the TrustedHTML wrapper so the
 * result can be assigned to innerHTML without triggering the default TT policy.
 * When Trusted Types are NOT active, falls back to a plain string.
 */
function sanitizeToTrustedHTML(html: string, domPurifyConfig: DomPurifyConfig): string {
    if (window.trustedTypes) {
        return DOMPurify.sanitize(html, {
            ...domPurifyConfig,
            RETURN_TRUSTED_TYPE: true,
        }) as unknown as string;
    }
    return DOMPurify.sanitize(html, domPurifyConfig);
}

/**
 * DOMPurify DOM element path: sanitize and return the root HTMLElement.
 */
function sanitizeToNode(html: string, domPurifyConfig: DomPurifyConfig): Node {
    return DOMPurify.sanitize(html, {
        ...domPurifyConfig,
        RETURN_DOM: true,
    });
}

/**
 * DOMPurify fragment path: sanitize and return a DocumentFragment.
 */
function sanitizeToDocumentFragment(
    html: string,
    domPurifyConfig: DomPurifyConfig
): DocumentFragment {
    return DOMPurify.sanitize(html, {
        ...domPurifyConfig,
        RETURN_DOM_FRAGMENT: true,
    });
}

/**
 * Dispatches to the appropriate DOMPurify function based on return type so
 * `getTrustedHTML` can delegate to either path depending on `shouldUseNativeSanitizer()`.
 */
export function sanitizeWithDomPurify<T extends SanitizerReturnType>(
    html: string,
    returnType: T,
    config: OwaSanitizerConfig
): string | HTMLElement | DocumentFragment | Node {
    const domPurifyConfig = buildDomPurifyConfig(config);
    switch (returnType) {
        case SanitizerReturnType.String:
            return sanitizeToString(html, domPurifyConfig);
        case SanitizerReturnType.TrustedHTML:
            return sanitizeToTrustedHTML(html, domPurifyConfig);
        case SanitizerReturnType.HTMLElement:
            return sanitizeToNode(html, domPurifyConfig) as HTMLElement;
        case SanitizerReturnType.Node:
            return sanitizeToNode(html, domPurifyConfig);
        case SanitizerReturnType.DocumentFragment:
            return sanitizeToDocumentFragment(html, domPurifyConfig);
        default:
            return sanitizeToString(html, domPurifyConfig);
    }
}
